import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, MapPin, Clock, CheckCircle2, AlertCircle, Send, Facebook, Instagram, Linkedin } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CONTACT } from "@/lib/assets";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOCIAL = [
  { icon: Facebook, href: CONTACT.social.facebook, label: "Facebook" },
  { icon: Instagram, href: CONTACT.social.instagram, label: "Instagram" },
  { icon: Linkedin, href: CONTACT.social.linkedin, label: "LinkedIn" },
];

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const InfoRow = ({ icon: Icon, label, value, href, testId }) => {
  const inner = (
    <>
      <span className="grid place-items-center h-12 w-12 rounded-full bg-emerald-brand/10 text-emerald-brand shrink-0">
        <Icon size={20} />
      </span>
      <span>
        <span className="block text-xs font-bold uppercase tracking-[0.18em] text-slate-400">{label}</span>
        <span className="block text-lg font-medium text-navy mt-0.5">{value}</span>
      </span>
    </>
  );
  return href ? (
    <a href={href} data-testid={testId} className="flex items-center gap-4 group hover:opacity-80 transition-opacity">{inner}</a>
  ) : (
    <div data-testid={testId} className="flex items-center gap-4">{inner}</div>
  );
};

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | success | error

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!form.email.trim()) e.email = "Please enter your email.";
    else if (!emailRe.test(form.email)) e.email = "Please enter a valid email address.";
    if (!form.message.trim()) e.message = "Please enter a message.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onChange = (k) => (ev) => {
    setForm((f) => ({ ...f, [k]: ev.target.value }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      setStatus("error");
    }
  };

  const field = "w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-navy placeholder:text-slate-400 outline-none transition-[border-color,box-shadow] duration-300 focus:border-emerald-brand focus:ring-4 focus:ring-emerald-brand/10";

  return (
    <>
      <PageHero
        eyebrow="Contact"
        titleLines={["Let's talk", "numbers"]}
        subtitle="Reach out for accounting services and financial advice — we'll get back to you shortly."
        testId="contact-hero"
      />

      <section data-testid="contact-section" className="bg-offwhite py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Info + map */}
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-heading font-bold text-navy text-3xl sm:text-4xl tracking-tight">Get in touch</h2>
            </Reveal>
            <div className="mt-10 space-y-7">
              <Reveal delay={0.05}><InfoRow icon={Phone} label="Phone" value={CONTACT.phoneDisplay} href={`tel:${CONTACT.phone}`} testId="contact-phone" /></Reveal>
              <Reveal delay={0.1}><InfoRow icon={MapPin} label="Address" value={CONTACT.address} testId="contact-address" /></Reveal>
              <Reveal delay={0.15}><InfoRow icon={Clock} label="Hours" value={CONTACT.hours} testId="contact-hours" /></Reveal>
            </div>

            <Reveal delay={0.2}>
              <div className="flex items-center gap-3 mt-10">
                {SOCIAL.map(({ icon: Icon, href, label }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    data-testid={`contact-social-${label.toLowerCase()}`}
                    className="grid place-items-center h-12 w-12 rounded-full border border-slate-200 text-navy transition-[transform,color,background-color,border-color] duration-300 hover:-translate-y-1 hover:bg-emerald-brand hover:text-navy-900 hover:border-emerald-brand"
                  >
                    <Icon size={19} />
                  </a>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.25}>
              <div className="mt-10 overflow-hidden rounded-2xl border border-slate-200 shadow-[0_20px_40px_-25px_rgba(9,30,54,0.25)]">
                <iframe
                  title="AGE-T location — Opal 52, Noord, Aruba"
                  data-testid="contact-map"
                  src="https://www.google.com/maps?q=Opal%2052%2C%20Noord%2C%20Aruba&output=embed"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </Reveal>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <Reveal y={50}>
              <div className="rounded-3xl bg-white border border-slate-200/80 p-8 sm:p-12 shadow-[0_30px_60px_-30px_rgba(9,30,54,0.25)]">
                <AnimatePresence mode="wait">
                  {status === "success" ? (
                    <motion.div
                      key="success"
                      data-testid="contact-success"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-center py-14"
                    >
                      <div className="mx-auto grid place-items-center h-20 w-20 rounded-full bg-emerald-brand/10 text-emerald-brand">
                        <CheckCircle2 size={40} />
                      </div>
                      <h3 className="mt-6 font-heading font-bold text-2xl text-navy">Thanks — we'll be in touch shortly.</h3>
                      <p className="mt-3 text-slate-500">Your message has been received. We appreciate you reaching out to AGE-T.</p>
                      <button
                        onClick={() => setStatus("idle")}
                        data-testid="contact-send-another"
                        className="mt-8 inline-flex items-center gap-2 rounded-full border border-navy/15 px-6 py-3 text-sm font-semibold text-navy hover:border-emerald-brand hover:bg-emerald-brand/5 transition-colors"
                      >
                        Send another message
                      </button>
                    </motion.div>
                  ) : (
                    <motion.form
                      key="form"
                      onSubmit={onSubmit}
                      data-testid="contact-form"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      noValidate
                    >
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-semibold text-navy mb-2">Name *</label>
                          <input
                            type="text"
                            value={form.name}
                            onChange={onChange("name")}
                            data-testid="contact-input-name"
                            className={field}
                            placeholder="Your name"
                          />
                          {errors.name && <p data-testid="error-name" className="mt-1.5 text-sm text-red-500 flex items-center gap-1"><AlertCircle size={14} />{errors.name}</p>}
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-navy mb-2">Email *</label>
                          <input
                            type="email"
                            value={form.email}
                            onChange={onChange("email")}
                            data-testid="contact-input-email"
                            className={field}
                            placeholder="you@example.com"
                          />
                          {errors.email && <p data-testid="error-email" className="mt-1.5 text-sm text-red-500 flex items-center gap-1"><AlertCircle size={14} />{errors.email}</p>}
                        </div>
                      </div>

                      <div className="mt-6">
                        <label className="block text-sm font-semibold text-navy mb-2">Phone</label>
                        <input
                          type="tel"
                          value={form.phone}
                          onChange={onChange("phone")}
                          data-testid="contact-input-phone"
                          className={field}
                          placeholder="Optional"
                        />
                      </div>

                      <div className="mt-6">
                        <label className="block text-sm font-semibold text-navy mb-2">Message *</label>
                        <textarea
                          rows={5}
                          value={form.message}
                          onChange={onChange("message")}
                          data-testid="contact-input-message"
                          className={`${field} resize-none`}
                          placeholder="How can we help?"
                        />
                        {errors.message && <p data-testid="error-message" className="mt-1.5 text-sm text-red-500 flex items-center gap-1"><AlertCircle size={14} />{errors.message}</p>}
                      </div>

                      {status === "error" && (
                        <p data-testid="contact-error" className="mt-5 flex items-center gap-2 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600">
                          <AlertCircle size={16} /> Something went wrong. Please try again or call us directly.
                        </p>
                      )}

                      <button
                        type="submit"
                        disabled={status === "loading"}
                        data-testid="contact-submit"
                        className="group mt-8 inline-flex items-center gap-3 rounded-full bg-emerald-brand px-8 py-4 text-sm font-bold uppercase tracking-widest text-navy-900 transition-[transform,box-shadow,background-color,opacity] duration-300 hover:-translate-y-1 hover:bg-emerald-light hover:shadow-[0_18px_40px_-12px_rgba(16,185,129,0.6)] disabled:opacity-60 disabled:translate-y-0"
                      >
                        {status === "loading" ? "Sending..." : "Send Message"}
                        <Send size={16} strokeWidth={2.5} className="transition-transform duration-300 group-hover:translate-x-1" />
                      </button>
                    </motion.form>
                  )}
                </AnimatePresence>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
